import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, fmtDate, Loading } from "@/components/ui-bits";
import { Sparkles, FileText, Wand2, Save, Copy, Check, Download, Mail, Loader2, X, Send, Search } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [
  "Rent Arrears Reminder Letter",
  "Rent Increase Letter - Section 13",
  "Tenancy Renewal Letter",
  "Assured Tenancy Agreement (Renters' Rights Act 2025)",
  "Garage / Parking Space Lease",
  "Lodger Agreement",
  "Licence to Occupy",
  "Guarantor Agreement",
  "Commercial / Business Lease",
  "Property Inspection Notice",
  "Section 8 Notice Cover Letter",
  "Notice to Quit",
  "End of Tenancy Letter",
  "Deposit Return Letter",
  "Deposit Deduction Letter",
  "Check-out Report Cover Letter",
  "Maintenance Update Letter",
  "Gas Safety Reminder Letter",
  "EICR / Electrical Safety Notice",
  "EPC Renewal Notice",
  "Right to Rent Check Request",
  "Welcome Letter to New Tenant",
  "Landlord Statement Cover Letter",
  "Landlord Onboarding / Terms of Business",
  "Management Agreement Letter",
  "Abandonment Notice",
  "Breach of Tenancy Warning Letter",
  "Noise / Nuisance Complaint Letter",
  "Service Charge Demand Letter",
  "Ground Rent Demand Letter",
  "Insurance Renewal Reminder",
  "Mortgage / Buy-to-Let Finance Cover Letter",
  "Late Payment / Overdue Invoice Letter",
  "Refund Confirmation Letter",
  "Contractor Instruction Letter",
  "Viewing Confirmation Letter",
  "Offer Acceptance Letter",
  "Sales Memorandum Cover Letter",
];

const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [form, setForm] = useState({ doc_type: DOC_TYPES[0], property_ref: "", recipient: "", instructions: "", new_rent: "", current_rent: "" });
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [email, setEmail] = useState({ to: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [matches, setMatches] = useState([]);

  const runSearch = async (e) => {
    if (e) e.preventDefault();
    if (!search.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.post("/documents/search", { query: search, types: DOC_TYPES });
      setMatches(data.matches || []);
      if ((data.matches || []).length) setForm((f) => ({ ...f, doc_type: data.matches[0] }));
      else toast.info("No matching document found");
    } catch { toast.error("Search failed"); }
    finally { setSearching(false); }
  };

  const load = () => api.get("/documents").then(({ data }) => setDocs(data));
  const [properties, setProperties] = useState([]);
  useEffect(() => {
    load();
    api.get("/properties").then(({ data }) => setProperties(data)).catch(() => {});
    Promise.all([api.get("/landlords").catch(() => ({ data: [] })), api.get("/contractors").catch(() => ({ data: [] }))])
      .then(([ll, cc]) => {
        const opts = [];
        (ll.data?.landlords || []).forEach((l) => { if (l.email) opts.push({ label: `${l.name} (Landlord)`, email: l.email }); });
        (cc.data || []).forEach((c) => { if (c.email) opts.push({ label: `${c.name} (Contractor)`, email: c.email }); });
        setRecipients(opts);
      });
  }, []);

  const openEmail = () => {
    setEmail({ to: "", subject: form.doc_type, message: `Please find attached your ${form.doc_type.toLowerCase()}.` });
    setEmailOpen(true);
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/documents/email", {
        doc_type: form.doc_type, property_ref: form.property_ref, recipient: form.recipient,
        body: output, to: email.to, subject: email.subject, message: email.message,
      });
      toast.success(`Emailed to ${email.to}`);
      setEmailOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not send email"); }
    finally { setSending(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await api.post("/documents/pdf",
        { doc_type: form.doc_type, property_ref: form.property_ref, recipient: form.recipient, body: output },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${form.doc_type.replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  const openSaved = (d) => {
    setForm((f) => ({ ...f, doc_type: d.doc_type || f.doc_type, property_ref: d.property_ref || "" }));
    setOutput(d.body || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success("Opened — view, download or email it");
  };

  const downloadSaved = async (d) => {
    try {
      const { data } = await api.post("/documents/pdf",
        { doc_type: d.doc_type, property_ref: d.property_ref || "", recipient: "", body: d.body || "" },
        { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(d.title || d.doc_type).replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate PDF"); }
  };

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOutput("");
    setProgress(6);
    setElapsed(0);
    const started = Date.now();
    const timer = setInterval(() => {
      setElapsed((Date.now() - started) / 1000);
      setProgress((p) => Math.min(95, p + Math.max(0.4, (95 - p) * 0.05)));
    }, 200);
    try {
      const { data } = await api.post("/documents/generate", form);
      setProgress(100);
      setOutput(data.body);
    } catch { toast.error("Generation failed. Please try again."); }
    finally {
      clearInterval(timer);
      setLoading(false);
      setTimeout(() => setProgress(0), 700);
    }
  };

  const save = async () => {
    await api.post("/documents", { title: form.doc_type, doc_type: form.doc_type, property_ref: form.property_ref, body: output });
    toast.success("Document saved");
    load();
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!docs) return <Layout title="Documents"><Loading /></Layout>;

  return (
    <Layout title="Document & Notice Centre" subtitle="AI Assisted">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2 p-6 h-fit">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={18} className="text-blue-700" />
            <h3 className="font-display text-lg font-semibold text-slate-900">Generate Correspondence</h3>
          </div>
          <p className="text-xs text-slate-400 mb-5">AI drafts UK-ready letters using your agency branding.</p>
          <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50/50 p-3" data-testid="doc-ai-search">
            <label className="block text-xs font-semibold text-blue-800 mb-1.5 flex items-center gap-1.5"><Sparkles size={13} /> AI document finder</label>
            <div className="flex gap-2">
              <input data-testid="doc-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch(e)}
                placeholder="Describe what you need, e.g. 'tenant is behind on rent'"
                className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <button type="button" data-testid="doc-search-btn" onClick={runSearch} disabled={searching}
                className="rounded-md bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">
                {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
              </button>
            </div>
            {matches.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="doc-search-results">
                {matches.map((m) => (
                  <button key={m} type="button" data-testid={`doc-match-${m.replace(/[^a-z0-9]+/gi, "-")}`}
                    onClick={() => setForm((f) => ({ ...f, doc_type: m }))}
                    className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${form.doc_type === m ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-200 hover:border-blue-300"}`}>
                    {m}
                  </button>
                ))}
              </div>
            )}
          </div>
          <form onSubmit={generate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Document Type</label>
              <select data-testid="doc-type-select" value={form.doc_type} onChange={(e) => setForm({ ...form, doc_type: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                {[...new Set([...matches, ...DOC_TYPES, form.doc_type].filter(Boolean))].map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            {form.doc_type.toLowerCase().includes("section 13") && (
              <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 space-y-3" data-testid="section13-fields">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">New rent (£ pcm)</label>
                    <input data-testid="doc-new-rent" type="number" value={form.new_rent} onChange={(e) => setForm({ ...form, new_rent: e.target.value })} placeholder="1750" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Current rent (£ pcm)</label>
                    <input data-testid="doc-current-rent" type="number" value={form.current_rent} onChange={(e) => setForm({ ...form, current_rent: e.target.value })} placeholder="1650" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  </div>
                </div>
                <p className="text-xs text-blue-700" data-testid="doc-effective-date">New rent takes effect from <b>{addDays(60)}</b> (auto-set to 60 days from today).</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Property</label>
                <select data-testid="doc-property-input" value={form.property_ref} onChange={(e) => setForm({ ...form, property_ref: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  <option value="">— Select property —</option>
                  {properties.map((p) => <option key={p.property_id} value={p.reference}>{p.address}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Recipient</label>
                <input data-testid="doc-recipient-input" value={form.recipient} onChange={(e) => setForm({ ...form, recipient: e.target.value })} placeholder="Mr J. Turner" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Instructions</label>
              <textarea data-testid="doc-instructions-input" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={3} placeholder="e.g. Rent of £1,650 is 14 days overdue. Polite but firm tone." className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            </div>
            <button data-testid="generate-doc-button" type="submit" disabled={loading} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Drafting… {elapsed.toFixed(0)}s</> : <><Wand2 size={16} /> Generate Draft</>}
            </button>
            {loading && (
              <div className="mt-3" data-testid="draft-progress-inline">
                <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-blue-600 transition-all duration-200 ease-out" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-xs text-slate-400 mt-1.5 text-center">{Math.round(progress)}% · {elapsed.toFixed(0)}s elapsed · usually about 8 seconds</p>
              </div>
            )}
          </form>
        </Card>

        <div className="lg:col-span-3 space-y-6">
          <Card className="p-6 min-h-[300px]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold text-slate-900">Draft</h3>
              {output && (
                <div className="flex gap-2">
                  <button onClick={copy} data-testid="copy-doc-button" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5 border border-slate-300 rounded-lg px-3 py-1.5">
                    {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
                  </button>
                  <button onClick={download} disabled={downloading} data-testid="download-doc-button" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5 border border-slate-300 rounded-lg px-3 py-1.5 disabled:opacity-60">
                    {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Download
                  </button>
                  <button onClick={openEmail} data-testid="email-doc-button" className="text-sm text-blue-700 hover:text-blue-900 flex items-center gap-1.5 border border-blue-200 rounded-lg px-3 py-1.5">
                    <Mail size={14} /> Email
                  </button>
                  <button onClick={save} data-testid="save-doc-button" className="text-sm bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-3 py-1.5 flex items-center gap-1.5">
                    <Save size={14} /> Save
                  </button>
                </div>
              )}
            </div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16" data-testid="draft-progress">
                <div className="w-full max-w-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700 flex items-center gap-2"><Wand2 size={15} className="text-blue-700" /> Drafting your letter…</span>
                    <span className="text-sm font-mono text-slate-500" data-testid="draft-progress-pct">{Math.round(progress)}%</span>
                  </div>
                  <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full bg-blue-600 transition-all duration-200 ease-out" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-xs text-slate-400 mt-2 text-center" data-testid="draft-progress-time">{elapsed.toFixed(0)}s elapsed · usually about 8 seconds</p>
                </div>
              </div>
            ) : output ? (
              <div>
                <textarea data-testid="doc-output" value={output} onChange={(e) => setOutput(e.target.value)} rows={22}
                  className="w-full whitespace-pre-wrap text-sm text-slate-700 leading-relaxed font-mono bg-slate-50 rounded-lg p-5 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-600 resize-y" />
                <p className="text-xs text-slate-400 mt-2">You can edit the letter above before copying, downloading, emailing or saving.</p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-slate-300">
                <FileText size={40} strokeWidth={1.2} />
                <p className="text-sm text-slate-400 mt-3">Your AI-generated letter will appear here.</p>
              </div>
            )}
          </Card>

          <Card className="p-6">
            <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Saved Documents</h3>
            <div className="space-y-2">
              {docs.length === 0 && <p className="text-sm text-slate-400">No saved documents yet.</p>}
              {docs.map((d) => (
                <div key={d.document_id} data-testid={`saved-doc-${d.document_id}`} className="flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0 group">
                  <button onClick={() => openSaved(d)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0 group-hover:bg-blue-100 transition-colors"><FileText size={16} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate group-hover:text-blue-700 transition-colors">{d.title}</p>
                      <p className="text-xs text-slate-400">{d.property_address || d.property_ref || "General"} · {fmtDate(d.date)}</p>
                    </div>
                  </button>
                  <Badge>{d.doc_type.split(" ")[0]}</Badge>
                  <button onClick={() => openSaved(d)} data-testid={`open-doc-${d.document_id}`} title="Open" className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-700 flex items-center justify-center shrink-0"><FileText size={14} /></button>
                  <button onClick={() => downloadSaved(d)} data-testid={`download-saved-${d.document_id}`} title="Download PDF" className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-700 flex items-center justify-center shrink-0"><Download size={14} /></button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {emailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setEmailOpen(false)}>
          <div data-testid="email-doc-modal" className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">Email document</h3>
              <button type="button" data-testid="close-email" onClick={() => setEmailOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={sendEmail} className="space-y-4">
              {recipients.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quick pick (landlord / contractor)</label>
                  <select data-testid="email-quickpick" onChange={(e) => setEmail({ ...email, to: e.target.value })} value=""
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                    <option value="">— Select a contact —</option>
                    {recipients.map((r, i) => <option key={i} value={r.email}>{r.label} · {r.email}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Send to (tenant, landlord or contractor)</label>
                <input data-testid="email-to" type="email" required value={email.to} onChange={(e) => setEmail({ ...email, to: e.target.value })} placeholder="recipient@email.com" className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subject</label>
                <input data-testid="email-subject" required value={email.subject} onChange={(e) => setEmail({ ...email, subject: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                <textarea data-testid="email-message" rows={3} value={email.message} onChange={(e) => setEmail({ ...email, message: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              </div>
              <p className="text-xs text-slate-400">The branded PDF is attached automatically.</p>
              <button type="submit" data-testid="send-email-button" disabled={sending} className="w-full bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-4 py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send email
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
